import { useEffect, useMemo, useState } from "react";
import {
  useCreateWorkOrder,
  useUpdateWorkOrder,
  useWorkOrder,
  type WorkOrderDetail,
} from "@/hooks/use-work-orders";
import { JOB_TYPES } from "@/lib/usg-constants";
import { type ResourceOption, ResourcePicker } from "./ResourcePicker";

type Mode = { kind: "create" } | { kind: "edit"; woId: string };

interface WorkOrderFormDialogProps {
  open: boolean;
  mode: Mode;
  onOpenChange: (open: boolean) => void;
}

interface FormState {
  woNumber: string;
  client: string;
  jobType: string;
  billable: boolean;
  startDate: string;
  endDate: string;
  resources: ResourceOption[];
}

const EMPTY_FORM: FormState = {
  woNumber: "",
  client: "",
  jobType: Object.keys(JOB_TYPES)[0] ?? "",
  billable: true,
  startDate: "",
  endDate: "",
  resources: [],
};

function detailToForm(detail: WorkOrderDetail): FormState {
  return {
    woNumber: detail.woNumber,
    client: detail.client,
    jobType: detail.jobType,
    billable: detail.billable,
    startDate: detail.startDate ?? "",
    endDate: detail.endDate ?? "",
    resources: detail.resources.map((r) => ({
      id: r.id,
      name: r.name,
      email: r.email,
      role: r.role,
    })),
  };
}

export function WorkOrderFormDialog({ open, mode, onOpenChange }: WorkOrderFormDialogProps) {
  const isEdit = mode.kind === "edit";
  const editId = mode.kind === "edit" ? mode.woId : null;

  const { data: detail, isLoading: detailLoading } = useWorkOrder(editId, { enabled: open });
  const createMutation = useCreateWorkOrder();
  const updateMutation = useUpdateWorkOrder();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  useEffect(() => {
    if (!open) return;
    if (isEdit && detail) {
      setForm(detailToForm(detail));
      setErrors({});
    } else if (!isEdit) {
      setForm(EMPTY_FORM);
      setErrors({});
    }
  }, [open, isEdit, detail]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  const jobTypeOptions = useMemo(() => Object.entries(JOB_TYPES), []);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!isEdit && !form.woNumber.trim()) next.woNumber = "WO number is required";
    if (!form.client.trim()) next.client = "Client is required";
    if (!form.jobType.trim()) next.jobType = "Job type is required";
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      next.endDate = "End date must be on or after start date";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isPending) return;
    if (!validate()) return;

    const payload = {
      client: form.client.trim(),
      jobType: form.jobType.trim(),
      billable: form.billable,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      resourceIds: form.resources.map((r) => r.id),
    };

    if (isEdit && editId) {
      updateMutation.mutate(
        { id: editId, input: payload },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      createMutation.mutate(
        { ...payload, woNumber: form.woNumber.trim() },
        { onSuccess: () => onOpenChange(false) },
      );
    }
  }

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (!isPending) onOpenChange(false);
      }}
    >
      <div
        className="modal wo-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal__header">
          <h2 className="modal__title">
            {isEdit ? `Edit Work Order ${detail?.woNumber ?? ""}` : "New Work Order"}
          </h2>
        </div>

        {isEdit && detailLoading ? (
          <div className="modal__body">
            <p className="t-muted">Loading…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal__body wo-form">
              <div>
                <p className="wo-form__section-title">Identity</p>
                <div className="wo-form__row">
                  <div className="wo-form__field">
                    <label htmlFor="wo-number">WO Number</label>
                    <input
                      id="wo-number"
                      type="text"
                      value={form.woNumber}
                      onChange={(e) => update("woNumber", e.target.value)}
                      disabled={isEdit}
                      placeholder="e.g. WO-2026-0042"
                    />
                    {errors.woNumber && (
                      <div className="wo-form__field-error">{errors.woNumber}</div>
                    )}
                  </div>
                  <div className="wo-form__field">
                    <label htmlFor="wo-jobtype">Job Type</label>
                    <select
                      id="wo-jobtype"
                      value={form.jobType}
                      onChange={(e) => update("jobType", e.target.value)}
                    >
                      {jobTypeOptions.map(([code, label]) => (
                        <option key={code} value={code}>
                          {code} — {label}
                        </option>
                      ))}
                    </select>
                    {errors.jobType && <div className="wo-form__field-error">{errors.jobType}</div>}
                  </div>
                </div>

                <div className="wo-form__field" style={{ marginTop: 12 }}>
                  <label htmlFor="wo-client">Client</label>
                  <input
                    id="wo-client"
                    type="text"
                    value={form.client}
                    onChange={(e) => update("client", e.target.value)}
                    placeholder="Client name"
                  />
                  {errors.client && <div className="wo-form__field-error">{errors.client}</div>}
                </div>

                <label className="wo-form__checkbox" style={{ marginTop: 12 }}>
                  <input
                    type="checkbox"
                    checked={form.billable}
                    onChange={(e) => update("billable", e.target.checked)}
                  />
                  Billable
                </label>
              </div>

              <div>
                <p className="wo-form__section-title">Schedule</p>
                <div className="wo-form__row">
                  <div className="wo-form__field">
                    <label htmlFor="wo-start">Start Date</label>
                    <input
                      id="wo-start"
                      type="date"
                      value={form.startDate}
                      onChange={(e) => update("startDate", e.target.value)}
                    />
                  </div>
                  <div className="wo-form__field">
                    <label htmlFor="wo-end">End Date</label>
                    <input
                      id="wo-end"
                      type="date"
                      value={form.endDate}
                      onChange={(e) => update("endDate", e.target.value)}
                      min={form.startDate || undefined}
                    />
                    {errors.endDate && <div className="wo-form__field-error">{errors.endDate}</div>}
                  </div>
                </div>
                <div className="wo-form__hint">Leave blank if the schedule isn't set yet.</div>
              </div>

              <div>
                <p className="wo-form__section-title">Assigned Engineers</p>
                <ResourcePicker
                  selected={form.resources}
                  onChange={(next) => update("resources", next)}
                />
              </div>
            </div>

            <div className="modal__footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn--primary" disabled={isPending}>
                {isPending ? "Saving…" : isEdit ? "Save changes" : "Create work order"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
